import { z } from 'zod';

export const monthLabelSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Tháng không hợp lệ');

export const teachingRoleSchema = z.enum(['CHINH', 'TRO_GIANG']);

/** One band. minStudents ≥ 1: a band never starts below one student, which
 *  is also what makes "a class of 0 has no band" true by definition rather
 *  than by a special case in the resolver. */
export const payRateRuleSchema = z.object({
  role: teachingRoleSchema,
  minStudents: z.number().int().min(1).max(1000),
  rate: z.number().int().min(0).max(100_000_000),
});

/**
 * Whole-state save: the dialog PUTs the complete band table, so there are no
 * per-row endpoints to keep consistent with each other. `rate` is GONE — the
 * legacy flat rate migrated into a rule and the column is no longer written
 * (a half-live column with one remaining writer is how a removed field comes
 * back to life).
 */
export const upsertPaySettingSchema = z.object({
  rateUnit: z.enum(['PER_SESSION', 'PER_HOUR']),
  baseSalary: z.number().int().min(0).max(1_000_000_000).nullable(),
  rules: z
    .array(payRateRuleSchema)
    .max(20)
    .superRefine((rules, ctx) => {
      const seen = new Set<string>();
      rules.forEach((r, i) => {
        const key = `${r.role}:${r.minStudents}`;
        if (seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            path: [i, 'minStudents'],
            message: 'Trùng luật: đã có mức này cho vai trò này',
          });
        }
        seen.add(key);
      });
    }),
});

export const createAdjustmentSchema = z.object({
  amount: z.number().int().min(-1_000_000_000).max(1_000_000_000).refine((v) => v !== 0, 'Số tiền phải khác 0'),
  label: z.string().min(1).max(100),
  note: z.string().max(500).nullable().optional(),
});

export type PayRateRuleInput = z.infer<typeof payRateRuleSchema>;
export type UpsertPaySettingInput = z.infer<typeof upsertPaySettingSchema>;
export type CreateAdjustmentInput = z.infer<typeof createAdjustmentSchema>;
